import type { FC, ReactNode } from 'react'
import type { DiagramKey } from '@/types/content'
import type { PageLink } from '@/content/services'
import type { BlueprintKind } from '@/components/site/blueprint'

import Link from 'next/link'
import Image from 'next/image'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import WindowDiagram from '@/components/sections/window-diagram'
import { revealDelay } from '@/lib/motion'
import Blueprint, { BLUEPRINT_CORNER } from '@/components/site/blueprint'

import { getImage } from '@/content/images'

interface OptionCardsProps {
    title: string
    intro?: string
    links: PageLink[]
    id?: string
    tone?: 'plain' | 'tinted'
    blueprint?: BlueprintKind
}

const OptionCards: FC<OptionCardsProps> = ({ title, intro, links = [], id = 'options', tone = 'plain', blueprint }): ReactNode => {
    if (!links.length) return null

    return (
        <section aria-labelledby={`${id}-heading`} className={cn('relative isolate overflow-hidden', tone === 'tinted' && 'bg-secondary/60')}>
            {blueprint && <Blueprint kind={blueprint} className={BLUEPRINT_CORNER} />}

            <div className='mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8'>
                <div className='max-w-3xl' data-reveal>
                    <h2 id={`${id}-heading`} className='text-3xl font-bold sm:text-4xl'>
                        {title}
                    </h2>

                    {intro && (
                        <p className='mt-3 text-lg text-muted-foreground'>
                            {intro}
                        </p>
                    )}
                </div>

                <ul className='mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3'>
                    {links.map((link, index) => {
                        const image = getImage(link.image)

                        return (
                            <li key={link.href} data-reveal style={revealDelay((index % 3) * 90)}>
                                <Link
                                    href={link.href}
                                    className='group flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary hover:shadow-lg'
                                >
                                    <div className='relative flex aspect-[16/10] items-center justify-center overflow-hidden bg-[#f3f7fb]'>
                                        {link.diagram
                                            ? <WindowDiagram diagram={link.diagram as DiagramKey} className='max-h-full w-3/4 transition-transform duration-500 group-hover:scale-105' />
                                            : (
                                                <Image
                                                    src={image.src}
                                                    alt=''
                                                    fill
                                                    sizes='(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw'
                                                    className='reveal-settle object-cover transition-transform duration-700 group-hover:scale-105'
                                                />
                                            )}
                                    </div>

                                    <div className='flex flex-1 flex-col p-6'>
                                        <h3 className='text-2xl font-bold transition-colors group-hover:text-primary'>
                                            {link.name}
                                        </h3>

                                        <p className='mt-2 flex-1 text-base'>
                                            {link.summary}
                                        </p>

                                        <span className='mt-4 inline-flex items-center gap-2 font-bold text-primary'>
                                            Learn more
                                            <ArrowRight className='size-5 transition-transform group-hover:translate-x-1' aria-hidden='true' />
                                        </span>
                                    </div>
                                </Link>
                            </li>
                        )
                    })}
                </ul>
            </div>
        </section>
    )
}

export default OptionCards
